'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from '@/components/ui/Link';

const ChevronRightIcon = ({ className = '' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

interface Props {
  sectionSlug: string;
  currentSlug: string;
}

/**
 * Fixed right-edge "next article" arrow for the dynamic (article-template)
 * article page — the AMP article page already has this (renderAmpNavArrows,
 * next -> first upcoming section article) and so does the legacy
 * ArticleListInfinite single-mode renderer (NavigationArrows, paging through
 * its own client-fetched combinedArticles) — this is the one gap: the
 * article-template renderer had no equivalent at all.
 *
 * Reuses the same `/api/articles?sectionSlug=` endpoint ArticleListInfinite's
 * own infinite-scroll fetch already calls, so no new backend work — fetches a
 * small batch of the current section's articles client-side (after the main
 * article has already painted, so it never blocks or slows the initial page
 * load) and links to the first one that isn't the article already showing.
 *
 * Only "next" is shown, matching AMP's asymmetric handling — a "previous"
 * arrow needs to know which article the reader came FROM (AMP carries that
 * via a `prev` query param set by the next-arrow link itself), which doesn't
 * exist yet on a freshly loaded article page.
 */
export default function NextArticleArrow({ sectionSlug, currentSlug }: Props) {
  const [nextArticle, setNextArticle] = useState<{ slug: string; headline: string } | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!sectionSlug) return;
    let cancelled = false;

    fetch(`/api/articles?sectionSlug=${encodeURIComponent(sectionSlug)}&offset=0&limit=5`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data?.articles) return;
        const match = data.articles.find((a: any) => a.story?.slug && a.story.slug !== currentSlug);
        if (match) setNextArticle({ slug: match.story.slug, headline: match.story.headline || '' });
      })
      .catch(() => { /* no next-article data — arrow simply doesn't render */ });

    return () => { cancelled = true; };
  }, [sectionSlug, currentSlug]);

  if (!isMounted || !nextArticle) return null;

  return createPortal(
    <Link
      href={`/${nextArticle.slug}`}
      aria-label={nextArticle.headline ? `அடுத்த செய்தி: ${nextArticle.headline}` : 'அடுத்த செய்தி'}
      title={nextArticle.headline || undefined}
      className="fixed right-0 top-1/2 -translate-y-1/2 z-[9999] flex items-center justify-center w-6 h-12 lg:w-10 lg:h-20 rounded-l-md border border-gray-300 bg-white text-012061 hover:bg-012061 hover:text-white cursor-pointer shadow-lg transition-all"
    >
      <ChevronRightIcon className="w-5 h-5 lg:w-7 lg:h-7" />
    </Link>,
    document.body
  );
}
